import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { ArrowUpRight, Check, MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import SEO from "@/components/site/SEO";
import { priorityCities, regionalCoverage } from "@/data/serviceArea";
import { breadcrumbSchema } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LEVELS = [
  { value: "listing-essential",  label: "Listing Essential quote" },
  { value: "listing-standard",   label: "Listing Standard quote" },
  { value: "listing-plus",       label: "Listing Plus quote" },
  { value: "media-walkthrough",  label: "Media Walkthrough Package quote" },
  { value: "ai-walkthrough",     label: "AI walkthrough quote" },
  { value: "video-walkthrough",  label: "Video walkthrough quote" },
  { value: "drone-addon",        label: "Drone add-on quote" },
  { value: "active-agent",       label: "Active Agent Plan quote" },
  { value: "team-standard",      label: "Team Standard quote" },
  { value: "brokerage-agency",   label: "Brokerage / Agency quote" },
  { value: "not-sure",           label: "Not sure yet" },
];

const INTERESTS = [
  "Property photography",
  "MLS-ready materials",
  "Website listing support",
  "Drone exterior capture",
  "Video walkthrough",
  "AI walkthrough",
  "Social media assets",
  "Feature sheet support",
  "Monthly listing support",
  "Team or brokerage support",
];

const initial = {
  business_name: "",
  contact_name: "",
  email: "",
  phone: "",
  location: "",
  website: "",
  monthly_listings: "",
  property_types: "",
  support_needed: "",
  presence_channels: "",
  current_handling: "",
  service_level: "",
  interests: [],
  in_service_area: "yes",
  preferred_timeline: "",
  property_address: "",
  notes: "",
};

export default function Contact() {
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = React.useState(() => {
    const sp = new URLSearchParams(window.location.search);
    const plan = sp.get("plan");
    const property = sp.get("property");
    const help = sp.get("help");
    const aud = sp.get("audience");
    const parts = [];
    if (property) parts.push(`Property type: ${property}`);
    if (help) parts.push(`Help needed: ${help}`);
    if (aud) parts.push(`Audience path: ${aud}`);
    return { ...initial, service_level: plan || "", notes: parts.join(" · ") };
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(null);

  const params = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const audience = params.get("audience");
  const audienceLabel = {
    agents: "For Agents & Teams",
    brokerage: "For Brokerages",
    builders: "For Builders & Developers",
    owners: "For Investors & Property Owners",
  }[audience];

  React.useEffect(() => {
    // Do NOT re-set service_level here (Radix Select is seeded via useState initializer).
    // This effect only refreshes the notes prefill if navigation params change while on /contact.
    const property = params.get("property");
    const help = params.get("help");
    if (!property && !help && !audience) return;
    setForm((f) => {
      if (f.notes) return f;
      const parts = [];
      if (property) parts.push(`Property type: ${property}`);
      if (help) parts.push(`Help needed: ${help}`);
      if (audience) parts.push(`Audience path: ${audience}`);
      return { ...f, notes: parts.join(" · ") };
    });
  }, [params, audience]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const toggleInterest = (label) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(label)
        ? f.interests.filter((x) => x !== label)
        : [...f.interests, label],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.email || !form.location) {
      toast.error("Please fill in name, email, and location.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        in_service_area: form.in_service_area === "yes",
        service_level: form.service_level || null,
        interests: form.interests,
      };
      const { data } = await axios.post(`${API}/leads`, payload);
      setSubmitted(data);
      toast.success("Request received. You'll get a confirmation shortly.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-brand-black text-white min-h-[80vh]">
        <SEO
          title="Request Received"
          description="Your LotWise Home listing support request has been received."
          path="/contact"
          noindex
        />
        <div className="mx-auto max-w-[1000px] px-5 md:px-10 py-24 md:py-32">
          <div className="eyebrow text-white/60">Confirmation</div>
          <h1 className="mt-4 text-[36px] md:text-6xl font-bold tracking-[-0.02em] leading-[1.02]" data-testid="confirmation-heading">
            Your request has been received.
          </h1>
          <p className="mt-8 max-w-2xl text-[16px] md:text-[17px] leading-relaxed text-white/70">
            LotWise Home will review your listing support request, location, property needs, and
            preferred service level. If you are inside the active southeastern Ontario service area,
            the next step is a short fit call or scheduling conversation. If you are outside the
            active area, your request will be added to the expansion waitlist.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-3 max-w-2xl">
            <div className="border border-white/12 p-5">
              <div className="eyebrow text-white/50">Reference</div>
              <div className="mt-2 text-[14px] font-mono text-white/90 break-all">{submitted.id}</div>
            </div>
            <div className="border border-white/12 p-5">
              <div className="eyebrow text-white/50">Status</div>
              <div className="mt-2 text-[14px] font-medium">
                {submitted.in_service_area ? "In service area — review in progress" : "Added to expansion waitlist"}
              </div>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <Link to="/pricing" className="inline-flex items-center gap-2 bg-white text-brand-black px-6 py-3.5 text-[13px] font-bold">
              View pricing <ArrowUpRight className="h-4 w-4" />
            </Link>
            <button
              onClick={() => { setSubmitted(null); setForm(initial); navigate("/contact"); }}
              className="inline-flex items-center gap-2 border border-white/25 text-white px-6 py-3.5 text-[13px] font-bold"
              data-testid="submit-another"
            >
              Submit another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white text-brand-black">
      <SEO
        title="Request Listing Support"
        description="Request real estate listing support from LotWise Home. Currently serving southeastern Ontario and accepting waitlist applications from other regions."
        path="/contact"
        schema={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Request Listing Support", path: "/contact" }])]}
      />
      {/* Header */}
      <section className="bg-brand-black text-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-20 md:py-28">
          <div className="eyebrow text-white/60">Request Listing Support</div>
          {audienceLabel && (
            <div className="mt-3 inline-flex items-center gap-2 border border-white/15 rounded-full px-3 py-1.5" data-testid="audience-badge">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-blue" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-medium text-white/80">{audienceLabel}</span>
            </div>
          )}
          <h1 className="mt-4 text-[36px] md:text-6xl font-bold tracking-[-0.02em] leading-[1.02] max-w-3xl">
            Tell us about your property.
          </h1>
          <p className="mt-6 max-w-2xl text-[16px] md:text-[17px] leading-relaxed text-white/70">
            A short form. We will review your request, service level, and location, and follow up
            with the next step. Currently serving southeastern Ontario. Waitlist accepted from all
            other regions.
          </p>
        </div>
      </section>

      {/* Form */}
      <section id="service-area" className="relative">
        <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none dot-grid-fade" aria-hidden />
        <form
          onSubmit={submit}
          className="relative mx-auto max-w-[1100px] px-5 md:px-10 py-16 md:py-24"
          data-testid="lead-form"
          noValidate
        >
          <div className="bg-white border border-brand-gray/25 p-6 md:p-10">
            {/* Section 1: Contact */}
            <div className="eyebrow-accent">Section 01</div>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">Who you are</h2>

            <div className="mt-6 grid md:grid-cols-2 gap-5">
              <Field label="Agent / team / brokerage name">
                <Input value={form.business_name} onChange={upd("business_name")} placeholder="e.g., Smith Real Estate Team" data-testid="field-business-name" />
              </Field>
              <Field label="Contact name" required>
                <Input value={form.contact_name} onChange={upd("contact_name")} placeholder="Full name" required data-testid="field-contact-name" />
              </Field>
              <Field label="Email" required>
                <Input type="email" value={form.email} onChange={upd("email")} placeholder="you@brokerage.com" required data-testid="field-email" />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={upd("phone")} placeholder="(613) 555-0123" data-testid="field-phone" />
              </Field>
              <Field label="Location" required>
                <Input value={form.location} onChange={upd("location")} placeholder="City, region" required data-testid="field-location" />
              </Field>
              <Field label="Website (optional)">
                <Input value={form.website} onChange={upd("website")} placeholder="https://" data-testid="field-website" />
              </Field>
            </div>

            {/* Section 2: Business context */}
            <div className="mt-14 eyebrow-accent">Section 02</div>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">Your listing flow</h2>

            <div className="mt-6 grid md:grid-cols-2 gap-5">
              <Field label="Listings handled in an average month">
                <Select value={form.monthly_listings} onValueChange={(v) => setForm((f) => ({ ...f, monthly_listings: v }))}>
                  <SelectTrigger data-testid="field-monthly-listings"><SelectValue placeholder="Select range" /></SelectTrigger>
                  <SelectContent>
                    {["0–1", "2–3", "4–6", "7–10", "11+"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Property types you list most often">
                <Input value={form.property_types} onChange={upd("property_types")} placeholder="e.g., single family, condo, waterfront" data-testid="field-property-types" />
              </Field>
              <Field label="Support you need">
                <Input value={form.support_needed} onChange={upd("support_needed")} placeholder="photos, listing details, website updates, all of the above" data-testid="field-support-needed" />
              </Field>
              <Field label="Where do your listings need to appear?">
                <Input value={form.presence_channels} onChange={upd("presence_channels")} placeholder="MLS, agent site, brokerage site, print, social" data-testid="field-channels" />
              </Field>
              <Field label="How are listing materials currently being handled?" className="md:col-span-2">
                <Textarea rows={3} value={form.current_handling} onChange={upd("current_handling")} placeholder="Briefly describe the current workflow" data-testid="field-current-handling" />
              </Field>
            </div>

            {/* Section 3: Fit */}
            <div className="mt-14 eyebrow-accent">Section 03</div>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">Fit and next step</h2>

            <div className="mt-6 grid md:grid-cols-2 gap-5">
              <Field label="Preferred service level">
                <Select value={form.service_level} onValueChange={(v) => setForm((f) => ({ ...f, service_level: v }))}>
                  <SelectTrigger data-testid="field-service-level"><SelectValue placeholder="Choose a level" /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_LEVELS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Preferred timeline">
                <Input value={form.preferred_timeline} onChange={upd("preferred_timeline")} placeholder="e.g., within 2 weeks, ongoing" data-testid="field-timeline" />
              </Field>

              <div className="md:col-span-2">
                <Label className="text-[11px] tracking-[0.14em] uppercase font-medium text-brand-gray">I am interested in</Label>
                <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="interests-group">
                  {INTERESTS.map((label) => {
                    const id = `interest-${label.toLowerCase().replace(/\s+/g, "-")}`;
                    const checked = form.interests.includes(label);
                    return (
                      <label
                        key={label}
                        htmlFor={id}
                        className={`flex items-center gap-3 border p-3 cursor-pointer transition-colors ${checked ? "border-brand-black" : "border-brand-gray/25 hover:border-brand-gray/50"}`}
                      >
                        <Checkbox
                          id={id}
                          checked={checked}
                          onCheckedChange={() => toggleInterest(label)}
                          data-testid={id}
                        />
                        <span className="text-[13.5px] text-brand-black">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <Field label="Property address or approximate area (optional)" className="md:col-span-2">
                <Input value={form.property_address} onChange={upd("property_address")} placeholder="Street or neighbourhood" data-testid="field-property-address" />
              </Field>
              <Field label="Notes about the upcoming listing" className="md:col-span-2">
                <Textarea rows={3} value={form.notes} onChange={upd("notes")} placeholder="Anything we should know" data-testid="field-notes" />
              </Field>

              <div className="md:col-span-2 mt-2">
                <Label className="text-[12px] tracking-[0.14em] uppercase text-brand-gray">Are you located in southeastern Ontario?</Label>
                <RadioGroup
                  value={form.in_service_area}
                  onValueChange={(v) => setForm((f) => ({ ...f, in_service_area: v }))}
                  className="mt-3 grid sm:grid-cols-2 gap-3"
                >
                  <label className={`flex items-center gap-3 border p-4 cursor-pointer ${form.in_service_area === "yes" ? "border-brand-black" : "border-brand-gray/25"}`}>
                    <RadioGroupItem value="yes" id="area-yes" data-testid="radio-in-area-yes" />
                    <div>
                      <div className="text-[14px] font-semibold">Yes — southeastern Ontario</div>
                      <div className="text-[12px] text-brand-gray">Route to active lead flow.</div>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 border p-4 cursor-pointer ${form.in_service_area === "no" ? "border-brand-black" : "border-brand-gray/25"}`}>
                    <RadioGroupItem value="no" id="area-no" data-testid="radio-in-area-no" />
                    <div>
                      <div className="text-[14px] font-semibold">No — I'll join the waitlist</div>
                      <div className="text-[12px] text-brand-gray">Applications from other regions welcomed.</div>
                    </div>
                  </label>
                </RadioGroup>
              </div>
            </div>

            <div className="mt-12 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-[12px] text-brand-gray max-w-md">
                Submitting sends your request to LotWise Home. We use it only to respond and coordinate listing support. See our{" "}
                <Link to="/privacy" className="text-brand-black link-underline">privacy policy</Link>.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-form-submit"
                className="group inline-flex items-center justify-center gap-2 bg-brand-black text-white px-7 py-4 text-[13px] font-bold tracking-tight hover:bg-brand-violet transition-colors disabled:opacity-60"
              >
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Send request <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
              </button>
            </div>
          </div>
        </form>
      </section>

      {/* Service Area cue */}
      <section className="bg-[#F7F7F5]">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-16 md:py-24">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-4">
              <div className="eyebrow-accent">Service Area</div>
              <h2 className="mt-3 text-2xl md:text-3xl font-bold tracking-tight">Currently serving southeastern Ontario.</h2>
              <p className="mt-4 text-[14.5px] text-brand-gray leading-relaxed">
                Priority coverage through {regionalCoverage.slice(0, 4).join(", ")}, and surrounding markets across the eastern Ontario corridor.
              </p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {priorityCities.map((c) => (
                <div key={c} className="border border-brand-gray/25 p-3 bg-white flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-brand-blue" />
                  <span className="text-[13px] font-medium">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[11px] tracking-[0.14em] uppercase font-medium text-brand-gray">
        {label}{required && <span className="text-brand-violet"> *</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
